import type { CostBreakdownFormValues, CostBreakdownProductsFormValues } from "../schemas"

import type { GoodsReceipt, GrCostBreakdownUpsertInput } from "@/graphql/codegen/graphql"

interface TransformCostBreakdownInput {
  goodsReceiptId: string
  goodsReceipt: GoodsReceipt
  grCostBreakdown: CostBreakdownFormValues
  productsCostBreakdown: CostBreakdownProductsFormValues
  isEditMode?: boolean
}

export const transformFormDataToPayload = (input: TransformCostBreakdownInput): GrCostBreakdownUpsertInput => {
  return {
    goodsReceiptId: input.goodsReceiptId,
    // Include grCostBreakdownId for edit mode
    grCostBreakdownId: input.isEditMode ? input.goodsReceipt.costBreakdown?.grCostBreakdownId : undefined,
    importFees: input.grCostBreakdown.importFees ?? undefined,
    insurance: input.grCostBreakdown.insurance ?? undefined,
    mpf: input.grCostBreakdown.mpf ?? undefined,
    hmf: input.grCostBreakdown.hmf ?? undefined,

    // Transform shipping costs - filter out empty entries and zero values
    shippingCosts: input.grCostBreakdown.shippingCosts
      .filter((cost) => cost.cost !== null && cost.cost !== undefined && cost.shippingType && cost.cost !== 0)
      .map((cost) => ({
        shippingType: cost.shippingType!,
        cost: cost.cost!,
      })),

    // Transform atypical surcharges - filter out empty entries and zero values
    atypicalSurcharges: input.grCostBreakdown.atypicalSurcharges
      .filter(
        (surcharge) =>
          surcharge.amount !== null &&
          surcharge.amount !== undefined &&
          surcharge.description.trim() &&
          surcharge.amount !== 0
      )
      .map((surcharge) => ({
        description: surcharge.description,
        amount: surcharge.amount!,
      })),

    // Transform products cost breakdown - iterate over ALL productDetails to ensure we send all lineBreakdowns
    lineBreakdowns: (input.goodsReceipt.productDetails || [])
      .map((productDetail) => {
        if (!productDetail?.goodsReceiptProductDetailId) {
          return null
        }

        // Get the corresponding form product by matching orderLineId
        // The form products are generated from purchase order lines using getGoodsReceiptPurchaseOrderLines utility
        // We need to find which form product index corresponds to this productDetail's orderLineId
        const purchaseOrderLines = input.goodsReceipt.orders?.flatMap((order) => order.orderLines || [])

        // Find which position this orderLineId appears in the purchase order lines array
        // This gives us the correct form product index since form products are generated in the same order
        const formProductIndex =
          purchaseOrderLines?.findIndex((orderLine) => orderLine.orderLineId === productDetail.orderLineId) ?? -1

        const product = formProductIndex !== -1 ? input.productsCostBreakdown.products[formProductIndex] : undefined

        // Find existing line cost breakdown ID for edit mode
        const existingLineCostBreakdown = input.isEditMode
          ? input.goodsReceipt.costBreakdown?.lineCostBreakdowns?.find(
              (line) => line.goodsReceiptProductDetailId === productDetail.goodsReceiptProductDetailId
            )
          : undefined

        // Transform tariffs - only include if there are valid entries
        const tariffs = product?.tariffs
          ?.filter((tariff) => tariff.amount !== null && tariff.amount !== undefined)
          ?.map((tariff) => ({
            amount: tariff.amount!,
          }))

        // Transform atypical surcharges - only include if there are valid entries and non-zero values
        const atypicalSurcharges = product?.atypicalSurcharges
          ?.filter(
            (surcharge) =>
              surcharge.amount !== null && surcharge.amount !== undefined && surcharge.type && surcharge.amount !== 0
          )
          ?.map((surcharge) => ({
            type: surcharge.type!,
            description: surcharge.description || "",
            amount: surcharge.amount!,
          }))

        return {
          // Use the actual goodsReceiptProductDetailId from the goods receipt
          goodsReceiptProductDetailId: productDetail.goodsReceiptProductDetailId,
          // Include grLineCostBreakdownId for edit mode
          grLineCostBreakdownId: existingLineCostBreakdown?.grLineCostBreakdownId,

          // Required field - provide default if not present (use 0 if no form product data)
          pricePerUom: product?.pricePerUom || 0,
          pricePerMetricUom: product?.pricePerMetricUom || undefined,
          bareCostPerUom: product?.bareCostPerUom || undefined,
          bareCostPerMetricUom: product?.bareCostPerMetricUom || undefined,
          totalBareCost: product?.totalBareCost || undefined,
          insurance: product?.insurance || undefined,

          duties: product?.duties?.amount || undefined,
          mpf: product?.mpf?.amount || undefined,
          hmf: product?.hmf?.amount || undefined,

          lastLaidDownCostPerUom: product?.lastLaidDownCostPerUom || undefined,

          isCompleted: !!product?.isCompleted,

          // Only include tariffs if there are valid entries, otherwise undefined
          tariffs: tariffs && tariffs.length > 0 ? tariffs : undefined,

          // Only include atypicalSurcharges if there are valid entries, otherwise undefined
          atypicalSurcharges: atypicalSurcharges && atypicalSurcharges.length > 0 ? atypicalSurcharges : undefined,
        }
      })
      .filter((lineBreakdown) => lineBreakdown !== null),
  }
}
